import React, { useState, useEffect, useCallback } from 'react';
import ScrollX from './ScrollX';
import { supabase } from '../supabase';
import { clickUnlessSelecting } from '../utils/clickGuard';
import AddEmployeeModal from './AddEmployeeModal';

// Shared NTRG staff directory (reads ntrg_staff). One component, two modes:
//   editable=true  → full CRUD (Table Management): + Add, row-click Edit, per-row Retire, show-inactive.
//   editable=false → read-only directory (Relationships → NTRG → Employee Information): active only.
export default function EmployeeTable({ darkMode, editable = false }) {
  const isDark = darkMode;
  const c = isDark ? {
    card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', rowHover: 'rgba(255,255,255,0.04)', th: 'rgba(26,34,56,0.5)',
  } : {
    card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', rowHover: 'rgba(0,0,0,0.02)', th: 'var(--line-soft)',
  };
  const s = {
    card: { background: c.card, border: `1px solid ${c.border}`, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)' },
    cardH: { padding: '12px 18px', borderBottom: `1px solid ${c.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    cardT: { fontSize: '14px', fontWeight: '700', color: c.text },
    th: { padding: '9px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: c.muted, background: c.th, borderBottom: `1px solid ${c.border}`, whiteSpace: 'nowrap' },
    td: { padding: '10px 16px', fontSize: '13px', fontWeight: '500', color: c.text, borderBottom: `1px solid ${isDark ? 'rgba(67,76,104,0.25)' : '#eef0f6'}`, whiteSpace: 'nowrap' },
    badge: (col) => ({ background: `rgba(${col},0.12)`, color: `rgb(${col})`, border: `1px solid rgba(${col},0.25)`, borderRadius: '20px', padding: '2px 9px', fontSize: '11px', fontWeight: '600' }),
    btn: { padding: '8px 14px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '7px', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
    btnGhost: { padding: '6px 12px', background: 'transparent', border: `1px solid ${c.border}`, borderRadius: '7px', color: c.muted, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: "'Public Sans',system-ui,sans-serif", whiteSpace: 'nowrap' },
  };

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editEmp, setEditEmp] = useState(null);

  const load = useCallback(() => {
    supabase.from('ntrg_staff').select('id, name, email, title, role, phone, reports_to, active, can_view_activity, can_close_questions').order('name').then(({ data }) => {
      setStaff(data || []);
      setLoading(false);
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleActive(emp) {
    const next = !emp.active;
    if (!window.confirm(next ? `Reactivate ${emp.name}?` : `Retire ${emp.name}? They'll be hidden unless "Show inactive" is on.`)) return;
    await supabase.from('ntrg_staff').update({ active: next }).eq('id', emp.id);
    setStaff(prev => prev.map(e => e.id === emp.id ? { ...e, active: next } : e));
  }

  const onSaved = () => { setAddOpen(false); setEditEmp(null); load(); };

  const visible = staff.filter(e => editable ? (showInactive || e.active) : e.active);

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;

  return (
    <div style={{ width: '100%' }}>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text, marginBottom: '6px' }}>{editable ? 'Employees' : 'Employee Information'}</div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '18px' }}>{editable ? 'Manage the NTRG staff directory.' : 'NTRG staff directory.'}</div>

      {editable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: c.muted, cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ cursor: 'pointer' }} />
            Show inactive
          </label>
          <button style={{ ...s.btn, marginLeft: 'auto' }} onClick={() => setAddOpen(true)}>+ Add Employee</button>
        </div>
      )}

      <div style={s.card}>
        <div style={s.cardH}><div style={s.cardT}>{editable ? 'NTRG Staff' : 'Directory'}</div><span style={{ fontSize: '12px', color: c.muted }}>{visible.length}</span></div>
        <ScrollX style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={s.th}>Name</th><th style={s.th}>Title</th><th style={s.th}>Role</th><th style={s.th}>Email</th><th style={s.th}>Phone</th><th style={{ ...s.th, textAlign: 'center' }}>Active</th>
              {editable && <th style={s.th}></th>}
            </tr></thead>
            <tbody>
              {visible.length === 0
                ? <tr><td colSpan={editable ? 7 : 6} style={{ ...s.td, textAlign: 'center', color: c.muted }}>No employees to show.</td></tr>
                : visible.map(e => (
                  <tr key={e.id}
                    onClick={editable ? clickUnlessSelecting(() => setEditEmp(e)) : undefined}
                    style={{ cursor: editable ? 'pointer' : 'default', opacity: e.active ? 1 : 0.6 }}
                    onMouseEnter={editable ? (ev => ev.currentTarget.style.background = c.rowHover) : undefined}
                    onMouseLeave={editable ? (ev => ev.currentTarget.style.background = 'transparent') : undefined}>
                    <td style={{ ...s.td, fontWeight: '600', color: editable ? 'var(--red)' : c.text }}>{e.name}</td>
                    <td style={{ ...s.td, color: c.muted }}>{e.title || '—'}</td>
                    <td style={s.td}>{e.role || '—'}</td>
                    <td style={{ ...s.td, color: c.muted }}>{e.email || '—'}</td>
                    <td style={{ ...s.td, fontFamily: "'DM Mono',monospace", fontSize: '12px' }}>{e.phone || '—'}</td>
                    <td style={{ ...s.td, textAlign: 'center' }}>{e.active ? <span style={s.badge('52,211,153')}>Active</span> : <span style={s.badge('248,113,113')}>Retired</span>}</td>
                    {editable && <td style={s.td}><button style={s.btnGhost} onClick={ev => { ev.stopPropagation(); toggleActive(e); }}>{e.active ? 'Retire' : 'Restore'}</button></td>}
                  </tr>
                ))}
            </tbody>
          </table>
        </ScrollX>
      </div>
      {(addOpen || editEmp) && <AddEmployeeModal isDark={isDark} employee={editEmp} allStaff={staff} onClose={() => { setAddOpen(false); setEditEmp(null); }} onSuccess={onSaved} />}
    </div>
  );
}
